import type { LocalizedDict } from '../index'

type ViewAsStrings = {
  title: string
  description: string
  switchLabel: string
  on: string
  off: string
  loadError: string
  saveError: string
  readOnlyNote: string
}

export const viewAsI18n: LocalizedDict<ViewAsStrings> = {
  en: {
    title: 'Customer panel view',
    description:
      "Lets a platform admin open a customer's own panel to see exactly what they see. "
      + 'Read-only: nothing can be changed from there. Off by default; turn it off again '
      + 'at any time and the system returns to its normal behaviour immediately.',
    switchLabel: 'Customer panel view (read-only)',
    on: 'On',
    off: 'Off',
    loadError: 'Could not read the setting — treating it as off.',
    saveError: 'Could not save the setting. Nothing changed.',
    readOnlyNote:
      'Only screens that declare themselves viewable open, and only for reading. '
      + 'Every view is recorded.',
  },
  tr: {
    title: 'Müşteri paneli görüntüleme',
    description:
      'Platform admin’inin bir müşterinin kendi panelini açıp tam olarak onun gördüğünü '
      + 'görmesini sağlar. Salt-okunur: oradan hiçbir şey değiştirilemez. Varsayılan kapalı; '
      + 'istediğin an kapatırsın ve sistem anında normal davranışına döner.',
    switchLabel: 'Müşteri paneli görüntüleme (salt-okunur)',
    on: 'Açık',
    off: 'Kapalı',
    loadError: 'Ayar okunamadı — kapalı kabul edildi.',
    saveError: 'Ayar kaydedilemedi. Hiçbir şey değişmedi.',
    readOnlyNote:
      'Yalnızca kendini görülebilir ilan eden ekranlar açılır ve yalnızca okuma için. '
      + 'Her bakış kayda geçer.',
  },
}

type ViewAsOverridesStrings = {
  title: string
  description: string
  addSeller: string
  addSupplier: string
  addAction: string
  pickerDescription: string
  empty: string
  sideSeller: string
  sideSupplier: string
  on: string
  off: string
  switchLabel: (orgName: string) => string
  remove: string
  removeLabel: (orgName: string) => string
  followsGlobal: string
  loadError: string
  saveError: string
}

export const viewAsOverridesI18n: LocalizedDict<ViewAsOverridesStrings> = {
  en: {
    title: 'Customer-specific setting',
    description:
      'A customer listed here ignores the general switch above. Leave the general switch off '
      + 'and add one customer as On to open only that panel; add a customer as Off to keep '
      + 'their panel closed even when the general switch is on.',
    addSeller: 'Add seller',
    addSupplier: 'Add supplier',
    addAction: 'Add',
    pickerDescription:
      'The customer is added with panel view On. You can switch it off or remove it afterwards.',
    empty: 'No customer-specific settings. Every customer follows the general switch.',
    sideSeller: 'Seller',
    sideSupplier: 'Supplier',
    on: 'On',
    off: 'Off',
    switchLabel: (orgName) => `Customer panel view for ${orgName}`,
    remove: 'Remove setting',
    removeLabel: (orgName) => `Remove the customer-specific setting for ${orgName}`,
    followsGlobal: 'Removing it returns the customer to the general switch.',
    loadError: 'Could not read the customer-specific settings.',
    saveError: 'Could not save. Nothing changed.',
  },
  tr: {
    title: 'Müşteriye özel ayar',
    description:
      'Burada listelenen müşteri yukarıdaki genel düğmeye bakmaz. Genel düğmeyi kapalı bırakıp '
      + 'bir müşteriyi Açık eklersen yalnızca onun paneli açılır; genel düğme açıkken bir '
      + 'müşteriyi Kapalı eklersen onun paneli kapalı kalır.',
    addSeller: 'Satıcı ekle',
    addSupplier: 'Üretici ekle',
    addAction: 'Ekle',
    pickerDescription:
      'Müşteri, panel görüntüleme Açık olarak eklenir. Sonra kapatabilir ya da kaldırabilirsin.',
    empty: 'Müşteriye özel ayar yok. Bütün müşteriler genel düğmeye göre davranır.',
    sideSeller: 'Satıcı',
    sideSupplier: 'Üretici',
    on: 'Açık',
    off: 'Kapalı',
    switchLabel: (orgName) => `${orgName} için müşteri paneli görüntüleme`,
    remove: 'Özel ayarı kaldır',
    removeLabel: (orgName) => `${orgName} için müşteriye özel ayarı kaldır`,
    followsGlobal: 'Kaldırınca müşteri yeniden genel düğmeye göre davranır.',
    loadError: 'Müşteriye özel ayarlar okunamadı.',
    saveError: 'Kaydedilemedi. Hiçbir şey değişmedi.',
  },
}
